import React from 'react';
import { createRoot } from 'react-dom/client';
import PropTypes from 'prop-types';
import SelectorButton from './SelectorButton';
import DataModel from '../../../models/dataModel';
import mapboxgl from 'mapbox-gl';

let mapMarkerEntries = [];

function renderCrosshairs() {
	const devMode = DataModel.getSettingsAttribute('devMode');
	if (devMode) {
		return (
			<div>
				<div className="map-crosshairs map-crosshairs-horizontal" />
				<div className="map-crosshairs map-crosshairs-vertical" />
			</div>
		);
	}
	return null;
}

function removeMapMarkers() {
	mapMarkerEntries.forEach(({ marker, popup, root }) => {
		if (marker) {
			marker.remove();
		}
		if (popup) {
			popup.remove();
		}
		if (root) {
			requestAnimationFrame(() => root.unmount());
		}
	});
	mapMarkerEntries = [];
}

function addMapMarkers(map, activeID, setMedia, playVideo) {
	const children = DataModel.getMediaChildren(activeID) || [];
	children.forEach((mediaID) => {
		const thumbnail = DataModel.getMediaThumbnail(mediaID);
		const long = DataModel.getMediaAttribute(mediaID, 'long', true);
		const lat = DataModel.getMediaAttribute(mediaID, 'lat', true);
		if (long && lat) {
			const markerElement = document.createElement('img');
			markerElement.className = 'marker';
			markerElement.src = thumbnail;
			const popupElement = document.createElement('div');
			const root = createRoot(popupElement);
			root.render(<SelectorButton mediaID={mediaID} setMedia={setMedia} playVideo={playVideo} hidden={true} />);
			const viewportWidth = document.documentElement.clientWidth;
			const offset = viewportWidth > 2000 ? 60 : 30;
			const popup = new mapboxgl.Popup({ offset, closeButton: false }).setDOMContent(popupElement);
			const marker = new mapboxgl.Marker(markerElement)
				.setLngLat([long, lat])
				.setPopup(popup)
				.addTo(map);
			mapMarkerEntries.push({ marker, popup, root });
		}
	});
}

function isInteractiveMap(mapID) {
	const image = DataModel.getMediaAttribute(mapID, 'image', true);
	return !image;
}

function handlePanMap(direction, map) {
	const panFactor = 80;
	if (direction === 'down') {
		map.panBy([0, panFactor], { duration: 110, easing: (x) => x });
	} else if (direction === 'up') {
		map.panBy([0, -panFactor], { duration: 110, easing: (x) => x });
	} else if (direction === 'right') {
		map.panBy([panFactor, 0], { duration: 110, easing: (x) => x });
	} else if (direction === 'left') {
		map.panBy([-panFactor, 0], { duration: 110, easing: (x) => x });
	}
}

function handleZoomMap(zoomType, map) {
	const zoomFactor = 0.2;
	const zoom = map.getZoom();
	let newZoom;
	if (zoomType === 'in') {
		newZoom = zoom + zoomFactor;
		map.zoomTo(newZoom, { duration: 110, easing: (x) => x });
	} else if (zoomType === 'out') {
		newZoom = zoom - zoomFactor;
		map.zoomTo(newZoom, { duration: 110, easing: (x) => x });
	}
}

function getMapboxObject(mapData, minZoom) {
	const mapboxObject = {
		container: 'mapbox',
		style: mapData.style,
		center: [mapData.long, mapData.lat],
		zoom: mapData.zoom,
		pitchWithRotate: false,
		scrollZoom: false,
		boxZoom: false,
		dragRotate: false,
		doubleClickZoom: false,
		keyboard: false,
		maxZoom: 18,
		minZoom: minZoom,
		attributionControl: false,
	};
	return mapboxObject;
}

class MapOnline extends React.PureComponent {
	constructor(props) {
		super(props);
		this.map = null;
		this.mapLoaded = false;
		this.mapMoveHandler = null;
		this.pendingApply = null;
		this.lastActiveMapID = null;
		this.command = '';
		this.interval = null;
	}

	componentDidUpdate(prevProps) {
		const { activeMapID, setMapState, setMedia, playVideo } = this.props;
		const wasInteractive = isInteractiveMap(prevProps.activeMapID);
		const isInteractive = isInteractiveMap(activeMapID);

		if (!isInteractive) {
			if (wasInteractive) {
				this.destroyMap();
			}
			return;
		}

		if (!this.map) {
			this.createMap(activeMapID, setMapState, setMedia, playVideo);
			return;
		}

		const mapChanged = prevProps.activeMapID !== activeMapID;
		if (mapChanged) {
			if (this.mapLoaded) {
				this.applyMapData(activeMapID, setMedia, playVideo, true);
			} else {
				this.pendingApply = { activeMapID, setMedia, playVideo, animate: true };
			}
			this.lastActiveMapID = activeMapID;
		} else if (this.mapLoaded) {
			removeMapMarkers();
			addMapMarkers(this.map, activeMapID, setMedia, playVideo);
		} else {
			this.pendingApply = { activeMapID, setMedia, playVideo, animate: false };
		}
	}

	componentDidMount() {
		const { activeMapID, setMapState, setMedia, playVideo } = this.props;
		if (isInteractiveMap(activeMapID)) {
			this.createMap(activeMapID, setMapState, setMedia, playVideo);
		}
		this.command = '';
		this.interval = setInterval(() => {
			if (!this.map || !this.command) {
				return;
			}
			if (this.command === 'up') {
				handlePanMap('up', this.map);
			} else if (this.command === 'down') {
				handlePanMap('down', this.map);
			} else if (this.command === 'left') {
				handlePanMap('left', this.map);
			} else if (this.command === 'right') {
				handlePanMap('right', this.map);
			} else if (this.command === 'in') {
				handleZoomMap('in', this.map);
			} else if (this.command === 'out') {
				handleZoomMap('out', this.map);
			}
		}, 110);
	}

	componentWillUnmount() {
		if (this.interval) {
			clearInterval(this.interval);
			this.interval = null;
		}
		this.command = '';
		this.destroyMap();
	}

	createMap(activeMapID, setMapState, setMedia, playVideo) {
		const mapData = DataModel.getMapData(activeMapID);
		mapboxgl.accessToken = mapData.accessToken;
		const minZoom = mapData.zoom < 9 ? mapData.zoom : 9;
		this.map = new mapboxgl.Map(getMapboxObject(mapData, minZoom));
		this.mapLoaded = false;
		this.mapMoveHandler = null;
		const devMode = DataModel.getSettingsAttribute('devMode');
		if (devMode) {
			this.mapMoveHandler = () => {
				if (!this.map) {
					return;
				}
				const center = this.map.getCenter();
				setMapState(center.lat, center.lng, this.map.getZoom());
			};
			this.map.on('move', this.mapMoveHandler);
			this.map.on('zoom', this.mapMoveHandler);
		}
		this.pendingApply = null;
		this.map.once('load', () => {
			if (!this.map) {
				return;
			}
			this.mapLoaded = true;
			const pending = this.pendingApply;
			this.pendingApply = null;
			const target = pending || {
				activeMapID: this.props.activeMapID,
				setMedia: this.props.setMedia,
				playVideo: this.props.playVideo,
				animate: true,
			};
			this.applyMapData(target.activeMapID, target.setMedia, target.playVideo, target.animate);
		});
		this.lastActiveMapID = activeMapID;
	}

	applyMapData(activeMapID, setMedia, playVideo, animate) {
		if (!this.map) {
			return;
		}
		if (!this.mapLoaded) {
			this.pendingApply = { activeMapID, setMedia, playVideo, animate };
			return;
		}
		const mapData = DataModel.getMapData(activeMapID);
		mapboxgl.accessToken = mapData.accessToken;
		const shouldResetView = animate || this.lastActiveMapID !== activeMapID;
		if (shouldResetView && activeMapID !== 'default' && mapData.long && mapData.lat) {
			this.map.stop();
			const minZoom = typeof this.map.getMinZoom === 'function' ? this.map.getMinZoom() : mapData.zoom;
			const baseZoom = Math.max(minZoom, mapData.zoom - 0.1);
			this.map.jumpTo({ center: [mapData.long, mapData.lat], zoom: baseZoom });
			if (animate) {
				this.map.easeTo({ zoom: mapData.zoom + 0.1, duration: 20000, easing: (x) => x });
			}
		}
		removeMapMarkers();
		addMapMarkers(this.map, activeMapID, setMedia, playVideo);
		this.lastActiveMapID = activeMapID;
		this.pendingApply = null;
	}

	destroyMap() {
		removeMapMarkers();
		if (this.map) {
			if (this.mapMoveHandler) {
				this.map.off('move', this.mapMoveHandler);
				this.map.off('zoom', this.mapMoveHandler);
				this.mapMoveHandler = null;
			}
			this.map.remove();
			this.map = null;
		}
		this.mapLoaded = false;
		this.pendingApply = null;
		this.lastActiveMapID = null;
		this.command = '';
	}

	render() {
		const { activeMapID } = this.props;
		const hideZoomButtons = DataModel.getMediaAttribute(activeMapID, 'hideZoomButtons', true);
		const image = DataModel.getMapImage(activeMapID, 'image');
		const altText = DataModel.getMediaAltText(activeMapID);
		let alt = altText[0];
		if (!alt) {
			alt = `${DataModel.getMediaTitle(activeMapID)} map`;
		} else {
			alt = `${altText}`;
		}
		const buttonStyle = hideZoomButtons ? { display: 'none' } : {};
		if (image) {
			return (
				<div className="image-area">
					<img className="bg-image" src={image} alt={alt} />
				</div>
			);
		}
		return (
			<div className="map-area" id="map-area">
				<p className="screen-reader">{alt}</p>
				<div id="mapbox" aria-hidden="true" className="absolute top right left bottom" style={{ width: '100%', height: '100%' }} />
				{renderCrosshairs()}
				<div aria-hidden="true" className="control-bar control-bar-map" style={buttonStyle}>
					<div className="buttons buttons-clear">
						<button
							aria-label="pan left"
							onTouchStart={() => (this.command = 'left')}
							onMouseDown={() => (this.command = 'left')}
							onKeyDown={(e) => {
								if (e.keyCode === 13 || e.keyCode === 32) {
									this.command = 'left';
								}
							}}
							onMouseUp={() => (this.command = '')}
							onKeyUp={() => (this.command = '')}
							onTouchEnd={() => (this.command = '')}>
							<i className="fas fa-arrow-circle-left" />
						</button>
						<button
							aria-label="pan down"
							onTouchStart={() => (this.command = 'down')}
							onMouseDown={() => (this.command = 'down')}
							onKeyDown={(e) => {
								if (e.keyCode === 13 || e.keyCode === 32) {
									this.command = 'down';
								}
							}}
							onMouseUp={() => (this.command = '')}
							onKeyUp={() => (this.command = '')}
							onTouchEnd={() => (this.command = '')}>
							<i className="fas fa-arrow-circle-down" />
						</button>
						<button
							aria-label="pan up"
							onTouchStart={() => (this.command = 'up')}
							onMouseDown={() => (this.command = 'up')}
							onKeyDown={(e) => {
								if (e.keyCode === 13 || e.keyCode === 32) {
									this.command = 'up';
								}
							}}
							onMouseUp={() => (this.command = '')}
							onKeyUp={() => (this.command = '')}
							onTouchEnd={() => (this.command = '')}>
							<i className="fas fa-arrow-circle-up" />
						</button>
						<button
							aria-label="pan right"
							onTouchStart={() => (this.command = 'right')}
							onMouseDown={() => (this.command = 'right')}
							onKeyDown={(e) => {
								if (e.keyCode === 13 || e.keyCode === 32) {
									this.command = 'right';
								}
							}}
							onMouseUp={() => (this.command = '')}
							onKeyUp={() => (this.command = '')}
							onTouchEnd={() => (this.command = '')}>
							<i className="fas fa-arrow-circle-right" />
						</button>
					</div>
					<div className="buttons buttons-clear">
						<button
							aria-label="zoom out"
							onTouchStart={() => (this.command = 'out')}
							onMouseDown={() => (this.command = 'out')}
							onKeyDown={(e) => {
								if (e.keyCode === 13 || e.keyCode === 32) {
									this.command = 'out';
								}
							}}
							onMouseUp={() => (this.command = '')}
							onKeyUp={() => (this.command = '')}
							onTouchEnd={() => (this.command = '')}>
							<i className="fas fa-minus-circle" />
						</button>
						<button
							aria-label="zoom in"
							onTouchStart={() => (this.command = 'in')}
							onMouseDown={() => (this.command = 'in')}
							onKeyDown={(e) => {
								if (e.keyCode === 13 || e.keyCode === 32) {
									this.command = 'in';
								}
							}}
							onMouseUp={() => (this.command = '')}
							onKeyUp={() => (this.command = '')}
							onTouchEnd={() => (this.command = '')}>
							<i className="fas fa-plus-circle" />
						</button>
					</div>
				</div>
			</div>
		);
	}
}

MapOnline.propTypes = {
	activeMapID: PropTypes.string,
	setMedia: PropTypes.func.isRequired,
	playVideo: PropTypes.func.isRequired,
	setMapState: PropTypes.func.isRequired,
};

export default MapOnline;
